import { InfraredFlight, Project } from "@nova/protobuf"
import {
    booleanValidator,
    boundingBoxValidators,
    colorValidators,
    createLayerValidator,
    demoIdValidators,
    enumValidator,
    UnionToTuple,
    updateLayerValidator,
} from "../util"
import {
    ColorSwatchCategory,
    hexColorSwatches,
    Integration,
    OrthoPalettes,
    HotspotPointTypes,
    scanAreaDefaultHexFillColor,
    scanAreaDefaultHexOutlineColor,
    DistanceUnit,
} from "@nova/constants"
import { dateAsNumber, encodeColorToNumber } from "../../util"
import { createNewLayerFractionalIndex } from "../../util/fractional_indexing"

export const createInfraredFlightValidator = createLayerValidator(InfraredFlight, {
    skippedProcessing: {
        validators: [
            {
                validator: (val) => val === !!val,
                error: "Skipped processing must be a boolean",
            },
        ],
        nonNull: true,
    },
    perimeterDistance: {
        validators: [
            {
                validator: (perimeterDistance) => perimeterDistance.range.length === 0,
                error: "Infrared flight cannot be made with perimeter range values",
            },
            {
                validator: (perimeterDistance) =>
                    Object.values(DistanceUnit).includes(perimeterDistance.unit as DistanceUnit),
                error: "Infrared flight requires valid perimeter distance unit",
            },
        ],
    },
})

export const updateInfraredFlightValidator = updateLayerValidator(InfraredFlight, {
    orthoBoundingBox: { validators: boundingBoxValidators, nonNull: true },
    orthoGradient: { validators: enumValidator("orthoGradient", OrthoPalettes), nonNull: true },
    pointColor: { validators: colorValidators },
    polyStroke: { validators: colorValidators },
    polyFill: { validators: colorValidators },
    pointType: { validators: enumValidator("pointType", HotspotPointTypes), nonNull: true },
    sensitivity: {
        validators: [
            {
                validator: (val) => val >= 0 && val <= 1,
                error: "Sensitivity is out of range",
            },
        ],
        nonNull: true,
    },
    perimeterDistance: {
        validators: [
            {
                validator: (val) => val.range.every((buffer) => buffer.distance !== null),
                error: "Hotspot distance buffer must be non-null",
            },
        ],
    },
    filters: {
        validators: [
            {
                validator: (val) => {
                    let valid = true

                    if (val.size) {
                        if (val.size.min !== undefined) {
                            if (val.size.min < 0) valid = false

                            if (val.size.max !== undefined) {
                                if (val.size.max <= val.size.min) valid = false
                            }
                        }
                    }

                    if (val.temperature) {
                        if (
                            val.temperature.min !== undefined &&
                            val.temperature.max !== undefined
                        ) {
                            if (val.temperature.max <= val.temperature.min) valid = false
                        }
                    }

                    return valid
                },
                error: "Size conditions: 0 <= min < max | Temperature conditions: min < max",
            },
        ],
    },
    relativeSizeEnabled: {
        validators: [
            {
                validator: () => true,
                error: "",
            },
        ],
    },
    hexIsFlat: {
        validators: [
            {
                validator: () => true,
                error: "",
            },
        ],
    },
    integrations: {
        validators: [
            {
                validator: (string) => {
                    try {
                        const json = JSON.parse(string)
                        let valid = true
                        ;(Object.values(json) as Integration[]).forEach(
                            (integration: Integration) => {
                                if (
                                    integration.updatedAt < 0 ||
                                    integration.updatedBy.userId.length !== 36
                                )
                                    valid = false
                            }
                        )

                        return valid
                    } catch {
                        return false
                    }
                },
                error: "updatedAt must be >= 0 and userId must be valid",
            },
        ],
    },
    mergeId: { validators: [] },
    scanAreaFillColor: { validators: colorValidators },
    scanAreaOutlineColor: { validators: colorValidators },
    processAfterUpload: {
        validators: booleanValidator("processAfterUpload must be a boolean value"),
    },
    demoId: {
        validators: demoIdValidators,
    },
})

export const defaultHotspotProperties = {
    pointType: HotspotPointTypes.Point,
    pointColor: encodeColorToNumber(hexColorSwatches[ColorSwatchCategory.Hotspot][0]),
    polyFill: encodeColorToNumber("#C87065BF"),
    polyStroke: encodeColorToNumber("#C87065"),
    orthoGradient: OrthoPalettes.WhiteHot,
    sensitivity: 0.7,
    perimeterDistance: { range: [], unit: DistanceUnit.Meters },
    scanAreaFillColor: encodeColorToNumber(scanAreaDefaultHexFillColor),
    scanAreaOutlineColor: encodeColorToNumber(scanAreaDefaultHexOutlineColor),
    processAfterUpload: true,
}

export const getDefaultInfraredFlightCreateArgs = (project: Project, inParentId?: string) => ({
    visible: true,
    createdAt: dateAsNumber(),
    images: [],
    orthoBoundingBox: [],
    index: createNewLayerFractionalIndex(project, 0, inParentId),
    hotspots: [],
    // parentId would never got autopopulated
    parentId: undefined,
    ...defaultHotspotProperties,
})

export type CreateInfraredFlightArgs = UnionToTuple<keyof typeof createInfraredFlightValidator>
export type UpdateInfraredFlightArgs = UnionToTuple<keyof typeof updateInfraredFlightValidator>
